import { useRouter } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, Circle, KeyRound, Loader2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const REQUIREMENTS = [
  { label: "At least 12 characters", test: (p: string) => p.length >= 12 },
  { label: "One uppercase letter", test: (p: string) => /[A-Z]/.test(p) },
  { label: "One lowercase letter", test: (p: string) => /[a-z]/.test(p) },
  { label: "One number", test: (p: string) => /[0-9]/.test(p) },
  { label: "One symbol", test: (p: string) => /[^A-Za-z0-9]/.test(p) },
];

export function ChangePasswordPage() {
  const router = useRouter();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allMet = REQUIREMENTS.every((r) => r.test(password));
  const mismatch = confirm.length > 0 && password !== confirm;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!allMet || mismatch) return;
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/auth/change-required-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ newPassword: password }),
      });
      if (!res.ok) {
        const data: { message?: string } = await res.json().catch(() => ({}));
        setError(data.message ?? "Failed to update password.");
        return;
      }
      router.navigate({ to: "/" });
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-slate-950 px-4">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/login-bg.jpg')" }}
      />
      <div className="absolute inset-0 bg-slate-950/80" />

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 flex flex-col items-center">
          <img
            src="/logo-white.png"
            alt="Prominent Builders"
            className="h-auto w-64 drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)]"
          />
        </div>

        <Card className="border-slate-700/50 bg-slate-800/90 shadow-2xl backdrop-blur">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="rounded-full bg-amber-500/10 p-2 ring-1 ring-amber-500/20">
                    <KeyRound className="size-4 text-amber-500" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">Set your new password</h2>
                </div>
                <p className="text-sm text-slate-400 pl-10">
                  Your password was set by an administrator. Please choose a new one to continue.
                </p>
              </div>

              {error && (
                <div className="flex items-start gap-2 rounded-lg bg-red-500/10 p-3 text-sm text-red-400 ring-1 ring-red-500/20">
                  <AlertCircle className="mt-0.5 size-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="cp-password" className="text-slate-300">
                    New password
                  </Label>
                  <Input
                    id="cp-password"
                    type="password"
                    autoFocus
                    placeholder="Create a strong password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="border-slate-600 bg-slate-700/50 text-white placeholder:text-slate-500 focus-visible:ring-[#415A77]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cp-confirm" className="text-slate-300">
                    Confirm password
                  </Label>
                  <Input
                    id="cp-confirm"
                    type="password"
                    placeholder="Re-enter your password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className={`border-slate-600 bg-slate-700/50 text-white placeholder:text-slate-500 focus-visible:ring-[#415A77] ${mismatch ? "border-red-500" : ""}`}
                  />
                  {mismatch && <p className="text-xs text-red-400">Passwords do not match.</p>}
                </div>
              </div>

              <div className="space-y-2 rounded-lg bg-slate-700/40 p-4">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                  Requirements
                </p>
                <ul className="space-y-1.5">
                  {REQUIREMENTS.map((r) => {
                    const met = r.test(password);
                    return (
                      <li key={r.label} className="flex items-center gap-2 text-sm">
                        {met ? (
                          <CheckCircle2 className="size-4 text-green-400 shrink-0" />
                        ) : (
                          <Circle className="size-4 text-slate-600 shrink-0" />
                        )}
                        <span className={met ? "text-white" : "text-slate-500"}>{r.label}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <Button
                type="submit"
                disabled={loading || !allMet || mismatch || !confirm}
                className="w-full bg-[#415A77] text-white hover:bg-[#4E6B8A]"
              >
                {loading ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
                Set Password & Continue
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
